import React, { useState, useEffect, useContext } from "react"
import PropTypes from "prop-types"
import i18n from "i18n"
import { useSnackbar } from "notistack"
import {
  Dialog,
  CircularProgress,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Tabs,
  Tab,
} from "@mui/material"
import IconButton from "@mui/material/IconButton"
import CloseIcon from "@mui/icons-material/Close"

import AuthContext from "context/auth/authContext"
import useEntitlementGuard from "hooks/useEntitlementGuard"
import { DYNAMIC_NUMERATION } from "constants/entitlements"
import NumerationBudgetsTab from "./NumerationBudgetsTab"
import SeriesTab from "./series/SeriesTab"

const NUMERATION_TYPE_DYNAMIC = "dynamic"
const NUMERATION_TYPE_STATIC = "static"

const ModalNumerationSettings = ({
  open,
  onClose,
  prefix1 = "",
  prefix2 = "",
  isDynamicNumberation = false,
  nextNumber = 1,
}) => {
  const { user, organizationFields, updateOrganizationNumeration } =
    useContext(AuthContext)
  const organizationId =
    organizationFields?.organizationId || user?.["custom:organizationId"] || ""
  const { enqueueSnackbar } = useSnackbar()

  const [tab, setTab] = useState(0)
  const [loading, setLoading] = useState(false)

  // Budgets (legacy) state — the only tab that still writes to organization fields.
  const [selected, setSelected] = useState(
    isDynamicNumberation ? NUMERATION_TYPE_DYNAMIC : NUMERATION_TYPE_STATIC,
  )
  const [localPrefix1, setLocalPrefix1] = useState(prefix1)
  const [localPrefix2, setLocalPrefix2] = useState(prefix2)
  const [localNextNumber, setLocalNextNumber] = useState(String(nextNumber))

  const [checkEntitlement, entitlementDialog] = useEntitlementGuard()

  useEffect(() => {
    if (!open) return
    setSelected(
      isDynamicNumberation ? NUMERATION_TYPE_DYNAMIC : NUMERATION_TYPE_STATIC,
    )
    setLocalPrefix1(prefix1)
    setLocalPrefix2(prefix2)
    setLocalNextNumber(String(nextNumber))
  }, [open, prefix1, prefix2, isDynamicNumberation, nextNumber])

  const handleNumerationTypeChange = e => {
    const value = e.target.value
    if (value === NUMERATION_TYPE_DYNAMIC) {
      if (
        !checkEntitlement(
          DYNAMIC_NUMERATION,
          i18n.t("numerationSettings.dynamicNumeration"),
        )
      ) {
        setSelected(NUMERATION_TYPE_STATIC)
        return
      }
    }
    setSelected(value)
  }

  const onSaveBudgets = async () => {
    if (!budgetsDirty) return
    setLoading(true)
    const normalizedNextNumber = Math.max(
      1,
      parseInt((localNextNumber ?? "").toString(), 10) || 1,
    )
    const body = {
      organizationId,
      prefix1: localPrefix1,
      prefix2: localPrefix2,
      next_number: normalizedNextNumber,
      is_dynamic_numeration: selected === NUMERATION_TYPE_DYNAMIC,
    }
    try {
      await updateOrganizationNumeration(body)
    } catch (error) {
      console.error("PATCH /organization-numeration error:", error)
      enqueueSnackbar(i18n.t("numerationSettings.saveError"), {
        variant: "error",
      })
    } finally {
      setLoading(false)
      onClose()
    }
  }

  const handleSaveClick = () => {
    if (budgetsDirty) {
      onSaveBudgets()
      return
    }
    onClose()
  }

  // Series tabs (invoices, purchase_orders) persist per-row via mutations.
  // Budgets is the only tab requiring an explicit Save; budgetsDirty controls
  // when the global Save button activates (state survives tab switches).
  const budgetsDirty =
    localPrefix1 !== (prefix1 ?? "") ||
    localPrefix2 !== (prefix2 ?? "") ||
    String(localNextNumber) !== String(nextNumber ?? 1) ||
    selected !==
      (isDynamicNumberation ? NUMERATION_TYPE_DYNAMIC : NUMERATION_TYPE_STATIC)

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {i18n.t("numerationSettings.numeration")}
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: theme => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Tabs
            value={tab}
            onChange={(_, newValue) => setTab(newValue)}
            aria-label="tabs numeration"
            sx={{ mb: 2 }}
          >
            <Tab label={i18n.t("numerationSettings.budgets")} />
            <Tab label={i18n.t("numerationSettings.invoices")} />
            <Tab label={i18n.t("numerationSettings.correctives")} />
            <Tab label={i18n.t("numerationSettings.purchaseOrders")} />
          </Tabs>
          {tab === 0 && (
            <NumerationBudgetsTab
              selected={selected}
              handleNumerationTypeChange={handleNumerationTypeChange}
              localPrefix1={localPrefix1}
              setLocalPrefix1={setLocalPrefix1}
              localPrefix2={localPrefix2}
              setLocalPrefix2={setLocalPrefix2}
              localNextNumber={localNextNumber}
              setLocalNextNumber={setLocalNextNumber}
            />
          )}
          {tab === 1 && (
            <SeriesTab organizationId={organizationId} serieType="invoices" />
          )}
          {tab === 2 && (
            <SeriesTab
              organizationId={organizationId}
              serieType="correctives"
            />
          )}
          {tab === 3 && (
            <SeriesTab
              organizationId={organizationId}
              serieType="purchase_orders"
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={onClose}
            color="branded"
            variant="outlined"
            disabled={loading}
          >
            {i18n.t("numerationSettings.cancel")}
          </Button>
          <Button
            onClick={handleSaveClick}
            color="branded"
            variant="contained"
            disabled={loading}
            sx={{ width: "106px", height: "36px" }}
          >
            {loading ? (
              <CircularProgress size={24} style={{ color: "white" }} />
            ) : (
              i18n.t("numerationSettings.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
      {entitlementDialog}
    </>
  )
}

ModalNumerationSettings.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  prefix1: PropTypes.string,
  prefix2: PropTypes.string,
  isDynamicNumberation: PropTypes.bool,
  nextNumber: PropTypes.number,
}

export default ModalNumerationSettings
